import React, { useState } from 'react';
import { Car, Check, MessageCircle, Mail } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { pricingTiers, vehicleTypes } from '../data/pricing';
import { BUSINESS_INFO } from '../utils/constants';

const Pricing = () => {
  const [selectedVehicle, setSelectedVehicle] = useState('medium');

  return (
    <div>
      <section className="section-blue" style={{padding: '4rem 0'}}>
        <div className="container text-center">
          <h1>Transparent Pricing</h1>
          <p style={{fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto'}}>
            Clear, upfront pricing with no hidden fees. Choose your vehicle size to see exact costs.
          </p>
        </div>
      </section>

      <section className="section section-white" style={{padding: '2rem 0', borderBottom: '1px solid var(--gray-200)'}}>
        <div className="container">
          <div className="text-center mb-4">
            <h2 style={{fontSize: '1.5rem'}}>Select Your Vehicle Type</h2>
            <p style={{color: 'var(--gray-600)'}}>Pricing varies by vehicle size</p>
          </div>
          <div className="pricing-selector">
            {vehicleTypes.map((type) => (
              <div
                key={type.id}
                onClick={() => setSelectedVehicle(type.id)}
                className={`pricing-option ${selectedVehicle === type.id ? 'active' : ''}`}
              >
                <Car />
                <div className="pricing-option-name">{type.name}</div>
                <div className="pricing-option-examples">{type.examples}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-cream">
        <div className="container">
          <div className="pricing-cards">
            {pricingTiers.map((tier) => {
              const isPopular = tier.id === 'silver';
              return (
                <Card 
                  key={tier.id}
                  hover 
                  className={`pricing-card ${isPopular ? 'popular' : ''}`}
                >
                  {isPopular && (
                    <div className="pricing-card-badge">MOST POPULAR</div>
                  )}

                  <div className="pricing-card-header">
                    <h3 className="pricing-card-name">{tier.name}</h3>
                    <div className="pricing-card-price">
                      £{tier.vehicles[selectedVehicle]}
                    </div>
                    <div className="pricing-card-vehicle">
                      for {vehicleTypes.find(v => v.id === selectedVehicle)?.name}
                    </div>
                  </div>

                  <a href={BUSINESS_INFO.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <Button 
                      variant={isPopular ? 'primary' : 'outline'} 
                      className="w-full"
                      icon={MessageCircle}
                    >
                      Book {tier.name}
                    </Button>
                  </a>
                </Card>
              );
            })}
          </div>

          <Card style={{marginTop: '3rem', padding: '2rem', margingBottom: '1rem'}}>
            <h3 style={{textAlign: 'center', marginBottom: '1.5rem'}}>What's Included in Every Service</h3>
            <div className="grid grid-3">
              <div className="flex gap-md">
                <Check size={24} color="var(--green-500)" style={{flexShrink: 0, marginLeft: '5rem'}} />
                <div>
                  <div style={{fontWeight: 600, marginBottom: '0.25rem'}}>Mobile Service</div>
                  <div style={{fontSize: '0.875rem', color: 'var(--gray-600)'}}>We come to your location</div>
                </div>
              </div>
              <div className="flex gap-md">
                <Check size={24} color="var(--green-500)" style={{flexShrink: 0, marginLeft: '5rem'}} />
                <div>
                  <div style={{fontWeight: 600, marginBottom: '0.25rem'}}>Premium Products</div>
                  <div style={{fontSize: '0.875rem', color: 'var(--gray-600)'}}>Professional-grade solutions</div>
                </div>
              </div>
              <div className="flex gap-md">
                <Check size={24} color="var(--green-500)" style={{flexShrink: 0, marginLeft: '5rem'}} />
                <div>
                  <div style={{fontWeight: 600, marginBottom: '0.25rem'}}>Expert Technicians</div>
                  <div style={{fontSize: '0.875rem', color: 'var(--gray-600)'}}>Trained professionals</div>
                </div>
              </div>
            </div>
          </Card>

          <div className="text-center mt-8">
            <h3 style={{marginBottom: '1rem'}}>Need a Custom Quote?</h3>
            <p style={{marginBottom: '1.5rem', color: 'var(--gray-600)'}}>
              For fleet services, special vehicles, or custom packages, contact us for a tailored quote
            </p>
            <div className="flex gap-md justify-center" style={{flexWrap: 'wrap'}}>
              <a href={BUSINESS_INFO.whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button variant="whatsapp" size="lg" icon={MessageCircle}>
                  Get Quote on WhatsApp
                </Button>
              </a>
              <a href={`mailto:${BUSINESS_INFO.email}`}>
                <Button variant="outline" size="lg" icon={Mail}>
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;